import { createSelector } from '@reduxjs/toolkit';
import { Map as ImMap, List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';

import { immutableEntriesShallowEqual } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountsSelectors } from '../accounts';
import { categoriesSelectors } from '../categories';
import { type Category, CategoryTypeEnum } from '../categories/categoriesTypes';
import { chartOfAccountsUtils } from '../chart-of-accounts';
import type { ChartOfAccountProps } from '../chart-of-accounts/chartOfAccountsTypes';
import { getId } from '../core/resourceStoreTypes';
import { createIncrementalFilter } from '../core/selectorsHelpers';
import { dateSelectors } from '../date';
import { featureFlagsSelectors } from '../feature-flags';
import { scheduledTransactionsSelectors } from '../scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from '../transactions';
import type { TransactionProps, TransactionRecord } from '../transactions/transactionsTypes';
import { sortTransactions } from './transactionListUtils';

const isSpendingCat = (
  categoriesById: ImMap<string, Category>,
  coa: ChartOfAccountProps | null | undefined,
  amount: number,
) => {
  if (!coa?.id || chartOfAccountsUtils.isTransferCOA(coa)) return false;
  if (coa.type === 'UNCATEGORIZED') {
    if (amount >= 0) return false;
    return true;
  }
  const category = categoriesById.get(coa.id);
  if (!category) return false;
  return category.type !== CategoryTypeEnum.INCOME;
};

type TransactionListState = Parameters<typeof transactionsSelectors.getTransactionsById>[0];
const emptyTransactions = ImMap<string, TransactionRecord>();
const emptyRecentTransactions = ImmutableList<TransactionRecord>();
const getTransactionsLoadPending = (state: TransactionListState): boolean =>
  accountsSelectors.getLoadPending(state) ||
  categoriesSelectors.getLoadPending(state) ||
  transactionsSelectors.getLoadPending(state);

type TransactionMapSelector = (state: TransactionListState) => ImMap<string, TransactionRecord>;

// Guard the source selector before entering its calculation graph during initial loading.
const whenTransactionsLoaded =
  (selectTransactions: TransactionMapSelector): TransactionMapSelector =>
  (state) =>
    getTransactionsLoadPending(state) ? emptyTransactions : selectTransactions(state);

const getLoadedTransactions = whenTransactionsLoaded(transactionsSelectors.getTransactionsById);
const getLoadedTransactionResources = whenTransactionsLoaded(transactionsSelectors.getResourcesById);

const filterRecentCandidates = createIncrementalFilter(
  (transactions: ImMap<string, TransactionRecord>, test: (txn: TransactionRecord) => unknown) =>
    transactions.filter(test),
);

const getRecentTransactionCandidates = createSelector(
  [
    getTransactionsLoadPending,
    getLoadedTransactionResources,
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getTransactionAccountClassificationById,
    accountsSelectors.getGoalAccountIds,
    dateSelectors.getCurrentDate,
    dateSelectors.getCurrentDateString,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'exposeInvestmentTransactions') || 'HIDE',
    (state) => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
  ],
  (
    isLoadPending,
    txnList,
    categoriesById,
    accountsById,
    goalAccountIds,
    today,
    todayString,
    exposeInvestmentTransactions,
    hideConnectedLoanTransactions,
  ): ImMap<string, TransactionRecord> => {
    if (isLoadPending) {
      return emptyTransactions;
    }
    const fourDaysAgo = today.minus({ days: 4 }).toISODate();
    const inputs = [
      categoriesById,
      accountsById,
      goalAccountIds,
      today,
      todayString,
      exposeInvestmentTransactions,
      hideConnectedLoanTransactions,
    ];

    // Edits re-test only the transactions they changed.
    return filterRecentCandidates(txnList, inputs, (txn) => {
      if (
        txn.postedOn <= todayString &&
        fourDaysAgo &&
        txn.postedOn > fourDaysAgo && // recent
        // Apply the narrow date window before classifying the rest of the transaction history.
        transactionsSelectors.isTransactionOfTypes(
          txn,
          goalAccountIds,
          transactionsSelectors.txnTypesDefault,
          todayString,
        ) &&
        txn.type !== 'INVOICE' &&
        txn.type !== 'PAYMENT' &&
        !transactionsUtils.isUnacceptedScheduledTxn(txn) &&
        transactionsUtils.isExposedTransaction(txn, accountsById, exposeInvestmentTransactions) &&
        !(hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accountsById))
      ) {
        if (transactionsUtils.isSplitTxn(txn)) {
          // accept any split with a spending item
          let hasExpense = false;
          txn.split?.items?.forEach((item) => {
            if (isSpendingCat(categoriesById, item.coa, item.amount)) {
              hasExpense = true;
            }
          });
          return hasExpense;
        } else {
          return isSpendingCat(categoriesById, txn.coa, txn.amount);
        }
      }
      return false;
    });
  },
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
);

type TransactionDisplayOrder = Pick<TransactionProps, 'postedOn' | 'amount'>;

const compareTransactionsByDateAndAmount = (
  first: TransactionDisplayOrder,
  second: TransactionDisplayOrder,
): number => {
  if (first.postedOn > second.postedOn) return -1;
  if (first.postedOn === second.postedOn) return first.amount < second.amount ? -1 : 1;
  return 1;
};

/**
 * Web's Recent Transactions cards read full records, which stay fresh after metadata-only edits.
 * Mobile's Dashboard uses getRecentTransactionsSummary without invoking this full-record sort.
 */
export const getRecentTransactions = createSelector(
  [getRecentTransactionCandidates],
  (transactions): ImmutableList<TransactionRecord> =>
    transactions.size ? transactions.sort(compareTransactionsByDateAndAmount).toList() : emptyRecentTransactions,
);

export const RECENT_TRANSACTIONS_PREVIEW_LIMIT = 6;

export type RecentTransactionSummary = {
  readonly ids: ReadonlyArray<string>;
  readonly totalAmount: number;
  readonly count: number;
};

const equalRecentTransactionSummaries = (previous: RecentTransactionSummary, next: RecentTransactionSummary): boolean =>
  previous.count === next.count &&
  Object.is(previous.totalAmount, next.totalAmount) &&
  previous.ids.length === next.ids.length &&
  previous.ids.every((id, index) => id === next.ids[index]);

type RecentTransactionSummaryInput = TransactionDisplayOrder & { readonly id: string };

// Project only the recent, eligible set. Metadata changes must not trigger sorting and summing,
// but retaining these projections must never substitute stale full transaction records.
const getRecentTransactionSummaryInputs = createSelector(
  [getRecentTransactionCandidates],
  (transactions): ReadonlyArray<RecentTransactionSummaryInput> =>
    transactions
      .valueSeq()
      .toArray()
      .map((transaction) => ({
        id: getId(transaction),
        postedOn: transaction.postedOn,
        amount: transaction.amount,
      })),
  {
    memoizeOptions: {
      resultEqualityCheck: (
        previous: ReadonlyArray<RecentTransactionSummaryInput>,
        next: ReadonlyArray<RecentTransactionSummaryInput>,
      ): boolean =>
        previous.length === next.length &&
        previous.every(
          (transaction, index) =>
            transaction.id === next[index].id &&
            transaction.postedOn === next[index].postedOn &&
            Object.is(transaction.amount, next[index].amount),
        ),
    },
  },
);

/**
 * Mobile's Dashboard reads IDs/count/total; its row components select the displayed transaction details.
 * Sort the projected values here so metadata edits can reuse the summary independently of Web's full list.
 */
export const getRecentTransactionsSummary = createSelector(
  [getRecentTransactionSummaryInputs],
  (transactions): RecentTransactionSummary => {
    const ids: string[] = [];
    let totalAmount = 0;
    // Preserve the full-list summation order, including floating-point rounding.
    [...transactions].sort(compareTransactionsByDateAndAmount).forEach((transaction, index) => {
      if (index < RECENT_TRANSACTIONS_PREVIEW_LIMIT) ids.push(transaction.id);
      totalAmount += Number(transaction.amount);
    });
    return { ids, totalAmount, count: transactions.length };
  },
  {
    memoizeOptions: { resultEqualityCheck: equalRecentTransactionSummaries },
  },
);

// Unreviewed transactions before display sorting.
const getUnreviewedCandidates = createSelector(
  [
    getTransactionsLoadPending,
    getLoadedTransactions,
    accountsSelectors.getTransactionAccountClassificationById,
    dateSelectors.getCurrentDateString,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'exposeInvestmentTransactions') || 'HIDE',
    (state) => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
  ],
  (isLoadPending, txnList, accountsById, todayString, exposeInvestmentTransactions, hideConnectedLoanTransactions) =>
    isLoadPending
      ? txnList.clear()
      : txnList.filter(
          (txn) =>
            txn.isReviewed !== true &&
            !transactionsUtils.isInvoiceRelatedTransaction(txn) &&
            accountsById.get(txn.accountId ?? '')?.type !== 'GOAL' &&
            accountsById.get(txn.accountId ?? '')?.type !== 'ACCOUNTS_RECEIVABLE' &&
            !(txn.coa?.type === 'ACCOUNT' && accountsById.get(txn.coa.id ?? '')?.type === 'ACCOUNTS_RECEIVABLE') &&
            txn.postedOn <= todayString &&
            !transactionsUtils.isUnacceptedScheduledTxn(txn) &&
            transactionsUtils.isExposedTransaction(txn, accountsById, exposeInvestmentTransactions) &&
            !(hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accountsById)),
        ),
);

/** Total unreviewed without sorting the list. */
export const getUnreviewedTransactionCount = createSelector(
  [getUnreviewedCandidates],
  (candidates): number => candidates.size,
);

/** Unreviewed transactions without display sorting, for callers that establish their own order. */
export const getUnreviewedTransactionCandidates = createSelector(
  [getUnreviewedCandidates],
  (candidates): ImmutableList<TransactionRecord> => candidates.valueSeq().toList(),
);

export type ReviewBucketKey = 'last30' | 'uncategorized' | 'largeAmount' | 'businessIncome' | 'billsAndSubscriptions';

export type ReviewBucket = {
  key: ReviewBucketKey;
  count: number;
  // Signed sum (Number(txn.amount)) of the transactions in this bucket —
  // income positive, expenses negative.
  amount: number;
};

export type ReviewBucketsResult = {
  // Count of ALL unreviewed transactions in scope (drives the "Review all" button).
  totalUnreviewed: number;
  // Inclusive lower bound (ISO YYYY-MM-DD) of the last30 window. The web card
  // reuses this exact boundary for the register deeplink so the register's date
  // filter matches the last30 bucket count (single source of truth for the window).
  last30StartISO: string | null;
  buckets: ReviewBucket[];
};

type ReviewBucketsProps = {
  accountIds?: ImmutableList<string>;
  largePurchaseThreshold: number;
  // All business-income category ids to count — the known Business Income parent
  // category PLUS its descendants (expanded in the web layer, which owns the
  // categories tree). A transaction counts when its parent coa OR any split-line
  // coa is one of these ids, matching the register's `categories=id1,id2,…` filter.
  businessIncomeCategoryIds?: string[];
  // The card's 4th tile is either/or: business users (Simplifi Business entitlement)
  // get `businessIncome`, everyone else gets `billsAndSubscriptions`. Both are always
  // computed; this flag only picks which one the returned `buckets` array carries.
  // Defaults to true to preserve the original (business-income) behavior for callers
  // that don't pass it.
  businessEnabled?: boolean;
};

// Attention buckets for the dashboard "Review Transactions" card. Every bucket
// shares a base filter of UNREVIEWED (isReviewed !== true); each bucket then
// applies its own predicate. Buckets are INDEPENDENT (not mutually exclusive) —
// a single transaction can land in several of them.
//
// Sourced from the SAME scope-only selector the /transactions register uses
// (getTransactionItemsWithoutSplitsForRegisterScopeOnly), so the card's counts
// match the register it links into — including now-reviewable INVOICE
// transactions, and excluding future-posted / out-of-scope ones. Deliberately
// NOT based on getUnreviewedTransactionCandidates, which excludes invoice/Accounts-
// Receivable transactions.
//
// The large-purchase threshold and business-income category id are passed as
// props (computed in the web layer) to keep this selector decoupled from the
// alert-rules / categories domains and easy to unit-test.
export const getReviewBuckets = createCachedSelector(
  [
    (state: RootState, props: ReviewBucketsProps) =>
      transactionsSelectors.getTransactionItemsWithoutSplitsForRegisterScopeOnly(state, {
        accountIds: props.accountIds ?? ImmutableList<string>(),
      }),
    dateSelectors.getCurrentDate,
    (_state: RootState, props: ReviewBucketsProps) => props.largePurchaseThreshold,
    (_state: RootState, props: ReviewBucketsProps) => props.businessIncomeCategoryIds,
    (_state: RootState, props: ReviewBucketsProps) => props.businessEnabled ?? true,
  ],
  (txnItemsById, today, largePurchaseThreshold, businessIncomeCategoryIds, businessEnabled): ReviewBucketsResult => {
    const last30Start = today.minus({ days: 29 }).toISODate();
    // Parent + descendant business-income category ids, for O(1) membership checks.
    const businessIncomeCategoryIdSet = new Set(businessIncomeCategoryIds ?? []);

    const buckets: Record<ReviewBucketKey, ReviewBucket> = {
      last30: { key: 'last30', count: 0, amount: 0 },
      uncategorized: { key: 'uncategorized', count: 0, amount: 0 },
      largeAmount: { key: 'largeAmount', count: 0, amount: 0 },
      businessIncome: { key: 'businessIncome', count: 0, amount: 0 },
      billsAndSubscriptions: { key: 'billsAndSubscriptions', count: 0, amount: 0 },
    };

    let totalUnreviewed = 0;

    const add = (bucket: ReviewBucket, amount: number) => {
      bucket.count += 1;
      bucket.amount += amount;
    };

    txnItemsById.forEach((txnItem) => {
      const txn = txnItem.transaction;
      if (txn.isReviewed === true) return;
      totalUnreviewed += 1;

      const amount = Number(txn.amount) || 0;
      const postedOn = txn.postedOn;
      const coa = txn.coa;

      if (postedOn && last30Start && postedOn >= last30Start) add(buckets.last30, amount);
      // Uncategorized: the true "Uncategorized" category on the parent OR on any split
      // line item, matching the register's `categories=UNCATEGORIZED` filter (which also
      // surfaces split transactions with an uncategorized line, but NOT opening-balance
      // coa.id '0' whose type is BALANCE_ADJUSTMENT).
      const isUncategorized =
        coa?.type === 'UNCATEGORIZED' || txn.split?.items?.some((item) => item.coa?.type === 'UNCATEGORIZED');
      if (isUncategorized) add(buckets.uncategorized, amount);
      // Large amount: absolute value over the threshold, either sign (income or expense),
      // matching the register's `Amount>N` quick filter which compares |amount|.
      if (Math.abs(amount) > largePurchaseThreshold) add(buckets.largeAmount, amount);
      // Business income: any of the business-income category ids (the known parent OR
      // one of its descendants) on the parent coa OR on any split line item, matching
      // the register's `categories=id1,id2,…` filter (which also surfaces split
      // transactions with a matching line) — same shape as the uncategorized bucket.
      const isBusinessIncome =
        businessIncomeCategoryIdSet.size > 0 &&
        ((!!coa?.id && businessIncomeCategoryIdSet.has(coa.id)) ||
          !!txn.split?.items?.some((item) => !!item.coa?.id && businessIncomeCategoryIdSet.has(item.coa.id)));
      if (isBusinessIncome) add(buckets.businessIncome, amount);
      // Bills & Subscriptions: parent-transaction flags (isBill / isSubscription),
      // matching the register's "Bills & Subscriptions" advanced filter
      // (`isBillOrSubscription=1`). Parent-level only — no split-line handling.
      if (transactionsUtils.isBillOrSubscription(txn)) add(buckets.billsAndSubscriptions, amount);
    });

    return {
      totalUnreviewed,
      last30StartISO: last30Start,
      // The 4th tile is either/or by subscription level: business users get
      // businessIncome, everyone else gets billsAndSubscriptions.
      buckets: [
        buckets.last30,
        buckets.uncategorized,
        buckets.largeAmount,
        businessEnabled ? buckets.businessIncome : buckets.billsAndSubscriptions,
      ],
    };
  },
)({
  keySelector: (_state: RootState, props: ReviewBucketsProps) =>
    `${props.accountIds ? props.accountIds.join(',') : 'all'}/${props.largePurchaseThreshold}/${
      props.businessIncomeCategoryIds?.join(',') ?? ''
    }/${props.businessEnabled ?? true}`,
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

function txnIsClose(txn1: TransactionProps, txn2: TransactionProps) {
  const sameSign = txn1?.amount < 0 === txn2?.amount < 0;
  const reasonableDate = DateTime.fromISO(txn1?.postedOn).diff(DateTime.fromISO(txn2?.postedOn), 'days')?.days;
  let sameType = true;
  if (txn2?.coa?.type === 'ACCOUNT') {
    sameType = txn2.coa.type === 'ACCOUNT' && txn1?.coa?.type === 'ACCOUNT';
  }
  return sameSign && sameType && Math.abs(reasonableDate) < 60;
}
/*
 * GetSortedTransactionsForAccountIds
 */
// THIS IS USED FOR THE MATCHING TXN LIST BOX
// filter out scheduled, matched, investment and user-entered transactions, then sort by date
export const getSortedTransactionsForAccountIds = createSelector(
  [
    (state, props: { matchTxn: TransactionProps | null | undefined }) => props.matchTxn,
    transactionsSelectors.getTransactionsByTextFilter,
  ],
  (matchTxn, transactionsForAccountIds) =>
    sortTransactions(
      transactionsForAccountIds.filter(
        (txn: TransactionProps | undefined | null) =>
          txn &&
          txn.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          txn.source !== 'SCHEDULED_TRANSACTION' &&
          !txn.stModelId &&
          txn.source !== 'USER_ENTERED' &&
          txn.type !== 'INVESTMENT' &&
          matchTxn &&
          txnIsClose(txn, matchTxn),
      ),
    ),
);

/*
 * GetLesserBilledTransactions
 */
export const getLesserBilledTransactions = createCachedSelector(
  (_state, props: { isBill?: boolean }) => props.isBill,
  (_state, props: { isTransfer?: boolean }) => props.isTransfer,
  scheduledTransactionsSelectors.getScheduledTransactions,
  transactionsSelectors.getTransactionsByTextFilter,
  categoriesSelectors.getIncomeCOAIds,
  (isBill, isTransfer, scheduledTxns, txnList, incomeCOAs) => {
    const endDate = DateTime.local();
    const startDateISO = endDate.minus({ months: 6 }).toISODate();
    const endDateISO = endDate.toISODate();
    const txns = txnList.filter((txn) => {
      let keepTransaction = true;
      const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
      if (txn.type !== 'CASH_FLOW') {
        keepTransaction = false;
      } else if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) {
        keepTransaction = false;
      } else if (isBill !== undefined && (isBill ? isIncome : !isIncome)) {
        keepTransaction = false;
        // If type is not ACCOUNT or it is not an external transfer or if it is not a credit card payment
      } else if (isTransfer === true && txn?.coa?.type !== 'ACCOUNT' && txn?.coa?.id !== '2' && txn?.coa?.id !== '3') {
        keepTransaction = false;
      } else if (txn.coa?.type === 'BALANCE_ADJUSTMENT' && txn.coa?.id === '0') {
        // exclude opening balance
        keepTransaction = false;
      } else {
        keepTransaction = txn.postedOn >= startDateISO && txn.postedOn <= endDateISO;
      }
      return keepTransaction;
    });

    return sortTransactions(txns);
  },
)({
  keySelector: (_state, props: { isBill?: boolean; isTransfer?: boolean }) =>
    `${props?.isBill ? 'is_bill' : 'not_bill'}/${props?.isTransfer ? 'is_transfer' : 'not_transfer'}`, // upcoming bills and upcoming income are both using selector at the same time so we need the cache to keep them from clobbering. also including isTransfer just in case
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getReviewBuckets, getLesserBilledTransactions);
